import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

const STORAGE_KEY = "rieave.popup.lastShownAt";

/**
 * Time-delayed discount popup. Shows after `delaySeconds` of activity on the
 * site, lets the visitor copy a coupon code, and respects a cooldown window
 * (so the same person isn't pestered every visit).
 *
 * Skips automatically when:
 *   - feature is disabled by admin
 *   - visitor is on cart / checkout (we don't interrupt active purchases)
 *   - cooldown hasn't elapsed since last show
 *   - visitor already dismissed this session
 *
 * Storage: localStorage `rieave.popup.lastShownAt` = ISO timestamp.
 */
const DiscountPopup = ({ config }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();

  const enabled = !!config?.enabled;
  const delay = Math.max(3, config?.delay_seconds || 35);
  const cooldownMs = Math.max(0, (config?.cooldown_hours || 24)) * 60 * 60 * 1000;
  const onCheckoutFlow = /^\/(cart|checkout|order|account|admin)/.test(location.pathname);

  useEffect(() => {
    // Reset whenever route changes — but only restart timer if conditions still hold
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!enabled || onCheckoutFlow) return;

    const last = localStorage.getItem(STORAGE_KEY);
    if (last) {
      const lastMs = new Date(last).getTime();
      if (!isNaN(lastMs) && Date.now() - lastMs < cooldownMs) return; // still in cooldown
    }

    timerRef.current = setTimeout(() => {
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    }, delay * 1000);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [enabled, delay, cooldownMs, onCheckoutFlow, location.pathname]);

  const close = () => setOpen(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(config.coupon_code || "");
      setCopied(true);
      toast.success(`Code "${config.coupon_code}" copied — paste it at checkout`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy. Long-press the code to select & copy.");
    }
  };

  if (!enabled) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="discount-popup"
        >
          <motion.div
            className="absolute inset-0 bg-charcoal/60 backdrop-blur-sm"
            onClick={close}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative max-w-md w-full bg-cream rounded-3xl shadow-2xl overflow-hidden"
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.85, y: 30, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            data-testid="discount-popup-card"
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center hover:bg-white shadow-sm"
              aria-label="Close"
              data-testid="discount-popup-close"
            >
              <X size={16} />
            </button>

            {/* Optional hero image */}
            {config.image_url ? (
              <div
                className="h-40 bg-cover bg-center"
                style={{ backgroundImage: `url(${config.image_url})` }}
                aria-hidden
              />
            ) : (
              <div className="h-32 bg-gradient-to-br from-rose/30 via-blush/40 to-lavender/40 flex items-center justify-center">
                <Sparkles size={42} className="text-rose" aria-hidden />
              </div>
            )}

            <div className="p-7 text-center">
              <p className="font-label text-[10px] uppercase tracking-[0.28em] text-rose mb-3">A gift for you</p>
              <h3 className="font-hero text-3xl text-charcoal leading-tight mb-3" data-testid="discount-popup-headline">
                {config.headline || "A little something before you go"}
              </h3>
              <p className="text-charcoal/70 mb-5 text-sm leading-relaxed">
                {config.subheadline || "Use this code at checkout for an extra discount."}
              </p>

              {/* Code box */}
              <div className="rounded-xl border-2 border-dashed border-rose/40 bg-white p-4 mb-4 flex items-center justify-between gap-3">
                <span
                  className="font-cormorant text-2xl tracking-[0.18em] text-charcoal select-all"
                  data-testid="discount-popup-code"
                >
                  {config.coupon_code || "WELCOME10"}
                </span>
                <button
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label uppercase tracking-[0.18em] bg-rose/15 text-rose hover:bg-rose/25 transition"
                  data-testid="discount-popup-copy"
                >
                  {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                </button>
              </div>

              <button
                onClick={() => { copy(); close(); }}
                className="w-full py-3.5 rounded-full bg-charcoal text-cream font-label text-[12px] uppercase tracking-[0.24em] hover:bg-charcoal/90 transition"
                data-testid="discount-popup-cta"
              >
                {config.button_text || "Copy code & shop"}
              </button>
              <button
                onClick={close}
                className="block w-full mt-2 py-2 text-charcoal/55 text-xs hover:text-charcoal"
                data-testid="discount-popup-dismiss"
              >
                No thanks, I'll pay full price
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DiscountPopup;
